import { useCallback, useEffect, useRef, useState } from "react";
import { resolveSolarCycleAppearance, type SolarCycleResolution } from "./solar-cycle";

type UseSolarCycleAppearanceOptions = {
  enabled: boolean;
};

type UseSolarCycleAppearanceResult = {
  resolution: SolarCycleResolution;
};

const ACTIVE_BLEND_TICK_MS = 1_000;

function getInitialResolution(): SolarCycleResolution {
  return resolveSolarCycleAppearance(new Date());
}

function isBlendInProgress(resolution: SolarCycleResolution): boolean {
  return (
    resolution.blend !== null && resolution.blend.progress > 0 && resolution.blend.progress < 1
  );
}

export function useSolarCycleAppearance({
  enabled,
}: UseSolarCycleAppearanceOptions): UseSolarCycleAppearanceResult {
  const [resolution, setResolution] = useState<SolarCycleResolution>(getInitialResolution);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearScheduledTransition = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const refreshResolution = useCallback(() => {
    setResolution(resolveSolarCycleAppearance(new Date()));
  }, []);

  const scheduleNextTransition = useCallback(
    (nextResolution: SolarCycleResolution) => {
      clearScheduledTransition();

      if (isBlendInProgress(nextResolution)) {
        timeoutRef.current = setTimeout(() => {
          refreshResolution();
        }, ACTIVE_BLEND_TICK_MS);
        return;
      }

      const delay = Math.max(nextResolution.nextTransitionAt.getTime() - Date.now(), 1_000);
      timeoutRef.current = setTimeout(() => {
        refreshResolution();
      }, delay);
    },
    [clearScheduledTransition, refreshResolution]
  );

  useEffect(() => {
    if (!enabled) {
      clearScheduledTransition();
      setResolution(getInitialResolution());
      return;
    }

    refreshResolution();
  }, [clearScheduledTransition, enabled, refreshResolution]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    scheduleNextTransition(resolution);
  }, [enabled, resolution, scheduleNextTransition]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshResolution();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, refreshResolution]);

  useEffect(() => {
    return () => {
      clearScheduledTransition();
    };
  }, [clearScheduledTransition]);

  return { resolution };
}
